import { useState, useEffect, useCallback, useMemo } from "react";
import InventariosService from "../api/inventarios.api";
import { getFiltrosInventariosIniciales } from "../constants";
import { contarPorEstado, filtrarPorEstado } from "../helpers/estadisticas";

/**
 * Hook para el listado de inventarios asignados al usuario autenticado (vista
 * del preparador), con filtros de fecha y almacén en servidor y estado y
 * estadísticas en cliente (mismo criterio que useInventarios).
 *
 * "Quién soy" lo resuelve InventariosService.listarMisInventarios() (a su vez
 * delegado en AuthService.getCurrentUser()): el hook nunca filtra a mano un
 * array completo por un id de usuario obtenido en el cliente.
 *
 * @returns {{
 *   inventarios: Array,
 *   estadisticas: {porEstado: Object<string, number>, total: number},
 *   filtros: Object,
 *   aplicarFiltros: Function,
 *   loading: boolean,
 *   error: string|null,
 *   recargar: Function
 * }} Estado del listado del preparador
 */
export const useMisInventarios = () => {
    const [todos, setTodos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtros, setFiltros] = useState(getFiltrosInventariosIniciales);

    const { fechaDesde, fechaHasta } = filtros;

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const data = await InventariosService.listarMisInventarios({ fechaDesde, fechaHasta });
            setTodos(Array.isArray(data) ? data : []);
            setError(null);
        } catch (err) {
            console.error("Error al cargar mis inventarios:", err);
            setError(err.message || "No se pudieron cargar los inventarios. Intenta nuevamente.");
        } finally {
            setLoading(false);
        }
    }, [fechaDesde, fechaHasta]);

    useEffect(() => {
        cargar();
    }, [cargar]);

    const inventarios = useMemo(() => filtrarPorEstado(todos, filtros.estado), [todos, filtros.estado]);
    const estadisticas = useMemo(() => contarPorEstado(todos), [todos]);

    /** Sustituye los filtros de una vez (fichas, Filtrar, Limpiar). */
    const aplicarFiltros = useCallback((nuevosFiltros) => {
        setFiltros({ ...getFiltrosInventariosIniciales(), ...nuevosFiltros });
    }, []);

    return { inventarios, estadisticas, filtros, aplicarFiltros, loading, error, recargar: cargar };
};
